import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  User,
  LoginRequest,
  LoginResponse,
  EmailVerificationRequest,
  MFASetupResponse,
  MFAVerifyRequest,
  MFAConfirmSetupRequest,
  AuthTokenResponse,
  ProfileUpdateRequest,
  PasswordChangeRequest,
  PasswordResetRequest,
  PasswordResetVerifyRequest,
  PasswordResetConfirmRequest,
  PasswordResetCodeResponse,
  MFAResetRequest,
  MFAResetVerifyCodeRequest,
  MFAResetVerifyRequest,
  MFAEmailCodeResponse,
  MFAStatusResponse,
  MFAChangeOtpStartResponse,
  UserCreateRequest,
  UserCreateResponse,
  UserUpdateRequest,
  UserListResponse,
  OrganizationDetails,
  OrganizationUpdateRequest,
  OrganizationUser,
  NotificationPreferences,
  ApiError,
  SuccessResponse,
  SelectOrganizationRequest,
} from '../types/api.types'

class ApiService {
  private api: AxiosInstance
  private static readonly TOKEN_KEY = 'auth_access_token'

  constructor() {
    // Frontend calls /api/* which proxy strips to backend's expected paths
    // Do NOT include /api prefix here as it causes double-prefix issues
    const baseURL = import.meta.env.VITE_API_URL || ''

    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor - inject Authorization header
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401) {
          // Skip redirect for auth endpoints to prevent infinite loops
          const isAuthEndpoint = error.config?.url?.includes('/auth/') ||
                                 error.config?.url?.includes('/password/');

          if (!isAuthEndpoint) {
            // Token expired - redirect to login
            // Store current path for redirect after login
            const currentPath = window.location.pathname;
            if (currentPath !== '/login') {
              sessionStorage.setItem('redirectAfterLogin', currentPath);
            }

            // Clear any auth state and redirect
            window.location.href = '/login';
          }
        }
        return Promise.reject(error)
      },
    )
  }

  // Authentication endpoints
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/api/auth/login', data)
    return response.data
  }

  async verifyEmail(data: EmailVerificationRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/api/auth/mfa/verify-email', data)
    return response.data
  }

  async getMFASetup(sessionToken: string): Promise<MFASetupResponse> {
    const response = await this.api.get<MFASetupResponse>(`/auth/api/auth/mfa/setup?session_token=${encodeURIComponent(sessionToken)}`)
    return response.data
  }

  async confirmMFASetup(data: MFAConfirmSetupRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/api/auth/mfa/confirm-setup', data)
    return response.data
  }

  async verifyMFA(data: MFAVerifyRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/api/auth/mfa/verify', data)
    return response.data
  }

  // Adopting an authenticator: verifying the TOTP persists it and completes the login.
  // There is no email confirmation step — the mailbox was proved during account setup.
  async enrollVerifyOTP(data: { session_token: string; totp_code: string }): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/api/auth/mfa/enroll/verify-otp', data)
    return response.data
  }

  async sendMFAEmailCode(sessionToken: string): Promise<MFAEmailCodeResponse> {
    const response = await this.api.post<MFAEmailCodeResponse>('/auth/api/auth/mfa/send-email-code', {
      session_token: sessionToken,
    })
    return response.data
  }

  async changePasswordRequired(data: {
    session_token: string
    current_password?: string
    new_password: string
  }): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/api/auth/change-password-required', data)
    return response.data
  }

  async getAccessToken(sessionToken: string): Promise<AuthTokenResponse> {
    // Exchange session token for access token after auth flow is complete
    const response = await this.api.post<AuthTokenResponse>('/auth/api/auth/token', { session_token: sessionToken })
    return response.data
  }

  async selectOrganization(data: SelectOrganizationRequest): Promise<AuthTokenResponse> {
    const response = await this.api.post<AuthTokenResponse>('/auth/api/auth/select-organization', data)
    return response.data
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/api/auth/logout')
    } finally {
      // Clear token and refresh timer regardless of API call success
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
      this.clearToken()
    }
  }

  // OAuth2 endpoints for authorization code flow with PKCE
  async authorize(data: {
    session_token: string
    return_url: string
    state: string  // Required (min 20 chars)
    code_challenge: string  // Required (min 43 chars)
    code_challenge_method?: string  // Defaults to "S256"
  }): Promise<{ code: string; redirect_url: string }> {
    const response = await this.api.post<{ code: string; redirect_url: string }>('/auth/api/auth/authorize', data)
    return response.data
  }

  async exchangeCode(code: string): Promise<AuthTokenResponse> {
    const response = await this.api.post<AuthTokenResponse>('/auth/api/auth/exchange', { code })
    return response.data
  }

  async refreshToken(): Promise<AuthTokenResponse> {
    const response = await this.api.post<AuthTokenResponse>('/auth/api/auth/refresh')
    return response.data
  }

  async getSessionFromCookie(): Promise<AuthTokenResponse> {
    // Restore session from SSO cookie (used when localStorage token is invalid)
    const response = await this.api.get<AuthTokenResponse>('/auth/api/auth/session', {
      withCredentials: true, // Include cookies
    })
    return response.data
  }

  // Token refresh management for sliding window
  private refreshTimer: NodeJS.Timeout | null = null
  private lastRefreshTime: number = 0

  /**
   * Schedule automatic token refresh 15 minutes before expiry.
   * Assumes JWT tokens are 30 minutes (1800 seconds).
   * Will refresh at the 15-minute mark (900 seconds).
   */
  scheduleTokenRefresh(): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
    }

    // If no token, don't schedule refresh
    const token = this.getToken()
    if (!token) {
      return
    }

    try {
      // Parse JWT to get expiry time
      const parts = token.split('.')
      if (parts.length !== 3) {
        return
      }

      const payload = JSON.parse(atob(parts[1]))
      const expiryTime = (payload.exp || 0) * 1000 // Convert to milliseconds
      const now = Date.now()
      const timeUntilExpiry = expiryTime - now

      // Refresh at 15 minutes (900 seconds) before expiry
      const refreshTime = Math.max(0, timeUntilExpiry - 900000)

      // Don't refresh more frequently than once per minute
      const timeSinceLastRefresh = now - this.lastRefreshTime
      if (timeSinceLastRefresh < 60000) {
        return
      }

      this.refreshTimer = setTimeout(() => {
        this.performTokenRefresh()
      }, refreshTime)
    } catch (error) {
      console.error('Error scheduling token refresh:', error)
    }
  }

  private async performTokenRefresh(): Promise<void> {
    try {
      const response = await this.refreshToken()
      if (response.access_token) {
        this.setToken(response.access_token)
        this.lastRefreshTime = Date.now()
        // Schedule the next refresh
        this.scheduleTokenRefresh()
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      // Let response interceptor handle 401 errors
    }
  }

  // Token management methods - using localStorage (persists across browser sessions)
  // This enables tokens to persist when users navigate between different ports/hosts
  setToken(token: string): void {
    localStorage.setItem(ApiService.TOKEN_KEY, token)
  }

  getToken(): string | null {
    return localStorage.getItem(ApiService.TOKEN_KEY)
  }

  clearToken(): void {
    localStorage.removeItem(ApiService.TOKEN_KEY)
  }

  async getMe(): Promise<User> {
    const response = await this.api.get<User>('/auth/api/auth/me')
    return response.data
  }

  // Profile endpoints - uses /api/auth routes
  async getProfile(): Promise<User> {
    const response = await this.api.get<User>('/auth/api/auth/me')
    return response.data
  }

  async updateProfile(data: ProfileUpdateRequest): Promise<User> {
    const response = await this.api.put<User>('/auth/api/auth/profile', data)
    return response.data
  }

  async changePassword(data: PasswordChangeRequest): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>('/auth/api/auth/change-password', data)
    return response.data
  }

  // Notification preferences (self-service) - uses /auth/api/auth/me routes
  async getNotificationPreferences(): Promise<NotificationPreferences> {
    const response = await this.api.get<NotificationPreferences>(
      '/auth/api/auth/me/notification-preferences',
    )
    return response.data
  }

  async updateNotificationPreferences(
    preferences: NotificationPreferences['preferences'],
  ): Promise<NotificationPreferences> {
    const response = await this.api.put<NotificationPreferences>(
      '/auth/api/auth/me/notification-preferences',
      { preferences },
    )
    return response.data
  }

  // MCIC access, answered by the MAAP app: the auth DB knows nothing about CICs, and
  // /app/api/* is behind forward auth, so the app resolves the caller's org itself.
  // A failed lookup reads as "no access".
  async hasMcicAccess(): Promise<boolean> {
    try {
      const response = await this.api.get<{ has_mcic: boolean }>('/app/api/cic/my-mcic-access', {
        withCredentials: true,
      })
      return response.data.has_mcic
    } catch {
      return false
    }
  }

  // Password reset endpoints - uses /auth/api/password routes
  async requestPasswordReset(data: PasswordResetRequest): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>('/auth/api/password/reset/request', data)
    return response.data
  }

  async verifyPasswordReset(data: PasswordResetVerifyRequest): Promise<PasswordResetCodeResponse> {
    const response = await this.api.post<PasswordResetCodeResponse>('/auth/api/password/reset/verify-code', data)
    return response.data
  }

  // Completing a reset also completes the login: the emailed code proved the mailbox and
  // the user just set the password, which is the same evidence a sign-in collects. The
  // response is therefore a login response, org selection and all.
  async confirmPasswordReset(data: PasswordResetConfirmRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/api/password/reset/verify', data)
    return response.data
  }

  async forgotUsername(email: string): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>('/auth/api/password/forgot-username', { email })
    return response.data
  }

  // MFA reset endpoints - uses /auth/api/mfa routes
  async requestMFAReset(data: MFAResetRequest): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>('/auth/api/mfa/reset/request', data)
    return response.data
  }

  async verifyMFAReset(data: MFAResetVerifyCodeRequest): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>('/auth/api/mfa/reset/verify', data)
    return response.data
  }

  async confirmMFAReset(data: MFAResetVerifyRequest): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>('/auth/api/mfa/reset/complete', data)
    return response.data
  }

  async getMFAStatus(): Promise<{ mfa_enabled: boolean; mfa_setup_completed: boolean }> {
    const response = await this.api.get<{ mfa_enabled: boolean; mfa_setup_completed: boolean }>(
      '/auth/api/mfa/status',
    )
    return response.data
  }

  // MFA method management (issue #213)
  async getMFAStatusFull(): Promise<MFAStatusResponse> {
    const response = await this.api.get<MFAStatusResponse>('/auth/api/mfa/status')
    return response.data
  }

  async mfaChangeSendCode(): Promise<MFAEmailCodeResponse> {
    const response = await this.api.post<MFAEmailCodeResponse>('/auth/api/mfa/change/send-code')
    return response.data
  }

  async mfaChangeToEmail(emailCode: string): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>('/auth/api/mfa/change/to-email', { email_code: emailCode })
    return response.data
  }

  async mfaChangeToOtpStart(): Promise<MFAChangeOtpStartResponse> {
    const response = await this.api.post<MFAChangeOtpStartResponse>('/auth/api/mfa/change/to-otp/start')
    return response.data
  }

  async mfaChangeToOtpComplete(data: {
    change_token: string
    totp_code: string
    email_code: string
  }): Promise<{ success: boolean; message: string; backup_codes?: string[] }> {
    const response = await this.api.post<{ success: boolean; message: string; backup_codes?: string[] }>(
      '/auth/api/mfa/change/to-otp/complete',
      data,
    )
    return response.data
  }

  async testAuthenticator(totpCode: string): Promise<{ valid: boolean }> {
    const response = await this.api.post<{ valid: boolean }>('/auth/api/mfa/test-authenticator', {
      totp_code: totpCode,
    })
    return response.data
  }

  // Organization management
  async switchOrganization(orgCode: string): Promise<AuthTokenResponse> {
    const response = await this.api.post<AuthTokenResponse>(`/auth/api/auth/switch-organization/${orgCode}`)
    return response.data
  }

  async getCurrentOrganization(): Promise<OrganizationDetails> {
    // Get organization from the current user's context via /auth/api/auth/me
    const user = await this.getMe()
    if (user.current_organization?.id) {
      return this.getOrganization(user.current_organization.id)
    }
    throw new Error('No organization associated with current user')
  }

  async getOrganization(orgId: number): Promise<OrganizationDetails> {
    const response = await this.api.get<OrganizationDetails>(`/auth/api/admin/organizations/${orgId}`)
    return response.data
  }

  async updateOrganization(orgId: number, data: OrganizationUpdateRequest): Promise<OrganizationDetails> {
    const response = await this.api.put<OrganizationDetails>(`/auth/api/admin/organizations/${orgId}`, data)
    return response.data
  }

  async getOrganizationUsers(orgId: number): Promise<OrganizationUser[]> {
    const response = await this.api.get<OrganizationUser[]>(`/auth/api/admin/organizations/${orgId}/users`)
    return response.data
  }

  async addUserToOrganization(orgId: number, userId: number, role: string): Promise<void> {
    await this.api.post(`/auth/api/admin/organizations/${orgId}/users/${userId}`, { role })
  }

  async changeUserRoleInOrganization(orgId: number, userId: number, role: string): Promise<void> {
    await this.api.put(`/auth/api/admin/organizations/${orgId}/users/${userId}/role`, { role })
  }

  // Admin user management endpoints - uses /admin/api routes
  async getUsers(page = 1, limit = 20, search = '', role?: string, isActive?: boolean): Promise<UserListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (search) params.append('search', search)
    if (role) params.append('role', role)
    if (isActive !== undefined) params.append('is_active', isActive.toString())

    const response = await this.api.get<UserListResponse>(`/auth/api/admin/users?${params}`)
    return response.data
  }

  async createUser(data: UserCreateRequest): Promise<UserCreateResponse> {
    const response = await this.api.post<UserCreateResponse>('/auth/api/admin/users', data)
    return response.data
  }

  async getUser(id: number): Promise<User> {
    const response = await this.api.get<User>(`/auth/api/admin/users/${id}`)
    return response.data
  }

  async updateUser(id: number, data: UserUpdateRequest): Promise<User> {
    const response = await this.api.put<User>(`/auth/api/admin/users/${id}`, data)
    return response.data
  }

  async deleteUser(id: number): Promise<void> {
    await this.api.delete(`/auth/api/admin/users/${id}`)
  }

  async resetUserPassword(id: number, newPassword: string): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>(`/auth/api/admin/users/${id}/reset-password`, {
      new_password: newPassword,
    })
    return response.data
  }

  async toggleUserActivation(id: number, isActive: boolean): Promise<User> {
    // Use activate or deactivate endpoint based on desired state
    const endpoint = isActive ? 'activate' : 'deactivate'
    const response = await this.api.post<User>(`/auth/api/admin/users/${id}/${endpoint}`)
    return response.data
  }

  async requestUserMFAReset(id: number): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>(`/auth/api/admin/users/${id}/mfa/reset/request`)
    return response.data
  }

  async clearUserMFA(id: number): Promise<SuccessResponse> {
    const response = await this.api.post<SuccessResponse>(`/auth/api/admin/users/${id}/mfa/clear`)
    return response.data
  }

  // Helper method to extract error message
  getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>
      const data = axiosError.response?.data

      // Handle various response formats
      if (data) {
        // Standard FastAPI error: {"detail": "message"}
        if (typeof data.detail === 'string') {
          return data.detail
        }
        // Validation errors: {"detail": [{"msg": "...", "type": "..."}]}
        if (Array.isArray(data.detail) && data.detail.length > 0) {
          return data.detail.map((e) => e.msg).join(', ')
        }
        // Password validation: {"detail": {"errors": [...]}}
        if (data.detail && typeof data.detail === 'object' && 'errors' in data.detail) {
          const errors = (data.detail as { errors: string[] }).errors
          if (Array.isArray(errors)) {
            return errors.join(', ')
          }
        }
        // Alternative format: {"message": "..."}
        if ('message' in data && typeof (data as { message?: string }).message === 'string') {
          return (data as { message: string }).message
        }
        // Response is just a string
        if (typeof data === 'string') {
          return data
        }
      }

      return axiosError.message
    }
    return 'An unexpected error occurred'
  }
}

// Export a singleton instance
export const apiService = new ApiService()